import { CONDITIONS_STALE_HOURS } from "./refresh-policy";
import { isUsableTimeZone, offsetIsoDate, spotLocalDate } from "./spot-locality";

/**
 * The shared freshness rule for a stored condition_scores row (lane fix/p3g-stale-rules,
 * docs/handover/phase-2c.md "Stale score rows and the shared freshness rule").
 *
 * A row may be presented as the resort's current score only while both hold:
 * - its last write is at most SCORE_STALE_AFTER_MS old (the boards' rule, isEntryStale in
 *   board.ts, which delegates here), and
 * - its `date`, the spot-local calendar date it was scored for, is the resort's own local date
 *   now. Only where the location has no usable IANA zone (spotLocalDate's bounded longitude
 *   fallback, which can run an hour behind the provider's day) is a freshly written row dated the
 *   local tomorrow accepted too. With a real zone, a row dated ahead of it (a catalogue zone that
 *   disagrees with the provider's) is not today's row, so it is stale until its date arrives.
 *
 * The end of currency is one instant, scoreRowCurrentUntilMs: the last millisecond at which the
 * row is current. Readers whose payload is cached (unstable_cache, up to 3 hours) either carry that
 * instant (the season overview) or the row's date and write (the spot pages, scoreRowStateAt) and
 * judge on the request clock, so no extra read is needed.
 */

/** Allowed age of a row's last write: the 3 hour refresh cadence plus 2 hours of slack (5 hours). */
export const SCORE_STALE_AFTER_MS = CONDITIONS_STALE_HOURS * 60 * 60 * 1000;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type ScoreRowStamp = Readonly<{
  /** The row's spot-local calendar date (condition_scores.date). */
  date: string | null | undefined;
  /** The row's last write (condition_scores.updated_at). */
  updatedAt: string | null | undefined;
}>;

export type ScoreRowLocality = Readonly<{
  timezone?: string | null;
  longitude?: number | null;
}>;

function locationFor(locality: ScoreRowLocality): { timezone: string | null; longitude: number } {
  const longitude = typeof locality.longitude === "number" && Number.isFinite(locality.longitude)
    ? locality.longitude
    : 0;
  return { timezone: locality.timezone ?? null, longitude };
}

/** The write-age half of the rule: true when the last write is missing, unreadable or too old. */
export function scoreWriteIsStale(updatedAt: string | null | undefined, nowMs: number): boolean {
  if (!updatedAt) return true;
  const writtenMs = new Date(updatedAt).getTime();
  if (Number.isNaN(writtenMs)) return true;
  return nowMs - writtenMs > SCORE_STALE_AFTER_MS;
}

/**
 * The first instant whose spot-local date is `date` or later (local midnight, or the first local
 * minute of the day where a clock change skips midnight). Uses spotLocalDate, so it agrees with
 * every other spot-local date in the app, including the bounded longitude fallback.
 */
export function spotLocalDayStartMs(date: string, locality: ScoreRowLocality): number {
  const location = locationFor(locality);
  const utcMidnight = Date.parse(`${date}T00:00:00Z`);
  // Every offset, real or fallback, lies between UTC-12 and UTC+14, so 15 hours before UTC
  // midnight the local date is still earlier and 13 hours after it the local date has arrived.
  let before = utcMidnight - 15 * HOUR_MS;
  let atOrAfter = utcMidnight + 13 * HOUR_MS;
  while (atOrAfter - before > MINUTE_MS) {
    const mid = before + Math.floor((atOrAfter - before) / 2 / MINUTE_MS) * MINUTE_MS;
    if (spotLocalDate(new Date(mid), location) >= date) atOrAfter = mid;
    else before = mid;
  }
  return atOrAfter;
}

/**
 * The last instant (epoch ms) at which the row may be presented as the resort's current score:
 * the earlier of its write plus SCORE_STALE_AFTER_MS and the millisecond before the resort's local
 * day after the row's date begins. Null when the row has no readable date or write stamp, so it is
 * never current.
 */
export function scoreRowCurrentUntilMs(row: ScoreRowStamp, locality: ScoreRowLocality): number | null {
  if (!row.updatedAt || !row.date || !ISO_DATE.test(row.date)) return null;
  const writtenMs = new Date(row.updatedAt).getTime();
  if (Number.isNaN(writtenMs)) return null;
  const nextDay = offsetIsoDate(row.date, 1);
  const byAge = writtenMs + SCORE_STALE_AFTER_MS;
  // The search only matters when the write would still be fresh at the earliest possible start
  // of the next local day (UTC+14); otherwise the write age decides on its own.
  if (byAge < Date.parse(`${nextDay}T00:00:00Z`) - 14 * HOUR_MS) return byAge;
  return Math.min(byAge, spotLocalDayStartMs(nextDay, locality) - 1);
}

/** Whether a carried current-until instant has passed (missing or null means never current). */
export function scoreCurrentUntilIsStale(currentUntilMs: number | null | undefined, nowMs: number): boolean {
  return typeof currentUntilMs !== "number" || !Number.isFinite(currentUntilMs) || nowMs > currentUntilMs;
}

/**
 * A row dated after the resort's local date now, where the location has a usable IANA zone: not
 * today's row (its date has not arrived), so never current. The longitude fallback accepts it.
 */
export function scoreRowIsAheadOfLocalDay(row: ScoreRowStamp, locality: ScoreRowLocality, nowMs: number): boolean {
  if (!row.date || !isUsableTimeZone(locality.timezone)) return false;
  return spotLocalDate(new Date(nowMs), locationFor(locality)) < row.date;
}

/** The shared rule at one instant: true when the row must not be presented as current. */
export function scoreRowIsStale(row: ScoreRowStamp, locality: ScoreRowLocality, nowMs: number): boolean {
  if (scoreRowIsAheadOfLocalDay(row, locality, nowMs)) return true;
  return scoreCurrentUntilIsStale(scoreRowCurrentUntilMs(row, locality), nowMs);
}

/**
 * The nightly gap (coordinator decision, 2 October 2026): from the resort's local midnight until
 * the first run that writes the new local day, the newest row is dated the day before. If that row
 * was still current when the day turned (stale only because its date ended, not because of its
 * write age), its stored forecast for the new date may be shown, labelled as a forecast with its
 * issue time, for at most this long after the row's last write. After that, "Score unavailable".
 */
export const GAP_FORECAST_MAX_AGE_MS = 12 * HOUR_MS;

/**
 * How a stored row may be presented at one instant:
 * - "current": as the resort's current score (scoreRowIsStale is false);
 * - "forecast": not current, but its stored forecast for `date` (the resort's new local date) may
 *   be shown as a forecast issued at `issuedAtMs` (the nightly gap, GAP_FORECAST_MAX_AGE_MS);
 * - "stale": neither.
 */
export type ScoreRowState =
  | Readonly<{ kind: "current" }>
  | Readonly<{ kind: "forecast"; date: string; issuedAtMs: number }>
  | Readonly<{ kind: "stale" }>;

export function scoreRowStateAt(row: ScoreRowStamp, locality: ScoreRowLocality, nowMs: number): ScoreRowState {
  const until = scoreRowCurrentUntilMs(row, locality);
  if (until === null || scoreRowIsAheadOfLocalDay(row, locality, nowMs)) return { kind: "stale" };
  if (nowMs <= until) return { kind: "current" };
  const writtenMs = new Date(row.updatedAt as string).getTime();
  // Stale because its local day ended while its write was still fresh (until is that day's end).
  if (writtenMs + SCORE_STALE_AFTER_MS <= until) return { kind: "stale" };
  if (nowMs - writtenMs > GAP_FORECAST_MAX_AGE_MS) return { kind: "stale" };
  const date = offsetIsoDate(row.date as string, 1);
  if (spotLocalDate(new Date(nowMs), locationFor(locality)) !== date) return { kind: "stale" };
  return { kind: "forecast", date, issuedAtMs: writtenMs };
}

/**
 * The rule carried without the location (lane fix/p3g-board-freshness): leaderboard snapshot
 * entries hold their row's date and write plus the instants the location's zone decides, so a
 * reader judges them on its own clock with no zone and no Intl call.
 * - currentUntilMs: scoreRowCurrentUntilMs with the location's zone (null: never current);
 * - currentFromMs: only for a row dated ahead of its location's local date when it was carried
 *   (scoreRowIsAheadOfLocalDay), the instant that date begins. Once a row's date has begun it stays
 *   begun, so a row that was not ahead when carried needs no such instant;
 * - gapUntilMs: only for a row written after its own local day ended (a late rewrite of an earlier
 *   day), the last instant of the day after its date, which bounds the nightly gap state. For a row
 *   written before its day ended, the 12-hour cap already ends the gap inside that day.
 */
export type CarriedScoreFreshness = Readonly<{
  date: string;
  /** The row's last write, normalised to an ISO instant (Date.prototype.toISOString). */
  updatedAt: string;
  currentUntilMs: number | null;
  currentFromMs?: number;
  gapUntilMs?: number;
}>;

/** The carried form of a row at `carriedAtMs` (when it is written into a snapshot), or null when its stamp is unreadable. */
export function carryScoreFreshness(
  row: ScoreRowStamp,
  locality: ScoreRowLocality,
  carriedAtMs: number,
): CarriedScoreFreshness | null {
  if (!row.date || !ISO_DATE.test(row.date) || !row.updatedAt) return null;
  const writtenMs = new Date(row.updatedAt).getTime();
  if (Number.isNaN(writtenMs)) return null;
  const ahead = scoreRowIsAheadOfLocalDay(row, locality, carriedAtMs);
  const currentUntilMs = scoreRowCurrentUntilMs(row, locality);
  const writtenAfterItsDay = currentUntilMs !== null && writtenMs > currentUntilMs;
  return {
    date: row.date,
    updatedAt: new Date(writtenMs).toISOString(),
    currentUntilMs,
    ...(ahead ? { currentFromMs: spotLocalDayStartMs(row.date, locality) } : {}),
    ...(writtenAfterItsDay ? { gapUntilMs: spotLocalDayStartMs(offsetIsoDate(row.date, 2), locality) - 1 } : {}),
  };
}

const STALE_STATE: ScoreRowState = Object.freeze({ kind: "stale" });
const CURRENT_STATE: ScoreRowState = Object.freeze({ kind: "current" });

/**
 * scoreRowStateAt on the carried form, for any instant at or after it was carried: the same
 * verdict, from the carried instants alone. The forecast state needs no local-date lookup here:
 * it starts when the row's local day ends (currentUntilMs is then the millisecond before the next
 * local day) and is capped at GAP_FORECAST_MAX_AGE_MS after a write made before that, well inside
 * the next local day; a write made after it carries gapUntilMs, the end of that next day.
 */
export function carriedScoreStateAt(carried: CarriedScoreFreshness, nowMs: number): ScoreRowState {
  const until = carried.currentUntilMs;
  const writtenMs = Date.parse(carried.updatedAt);
  if (typeof until !== "number" || !Number.isFinite(until) || Number.isNaN(writtenMs) || !ISO_DATE.test(carried.date)) {
    return STALE_STATE;
  }
  if (typeof carried.currentFromMs === "number" && nowMs < carried.currentFromMs) return STALE_STATE;
  if (nowMs <= until) return CURRENT_STATE;
  if (writtenMs + SCORE_STALE_AFTER_MS <= until) return STALE_STATE;
  if (nowMs - writtenMs > GAP_FORECAST_MAX_AGE_MS) return STALE_STATE;
  if (typeof carried.gapUntilMs === "number" && nowMs > carried.gapUntilMs) return STALE_STATE;
  return { kind: "forecast", date: offsetIsoDate(carried.date, 1), issuedAtMs: writtenMs };
}
